import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';

const COLLECTION_NAME = 'organizations';

/**
 * Generate a random invite code for an organization
 * @returns {string} - 8-character alphanumeric code
 */
const generateInviteCode = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excluding similar-looking characters
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

/**
 * Create a new organization
 * @param {Object} organizationData - Organization details
 * @param {string} createdByUid - User ID of the creator (org admin)
 * @returns {Promise<string>} - Organization ID
 */
export const createOrganization = async (organizationData, createdByUid, createdByName = '') => {
  try {
    const inviteCode = generateInviteCode();

    const orgRef = await addDoc(collection(db, COLLECTION_NAME), {
      organizationName: organizationData.organizationName,
      organizationDescription: organizationData.organizationDescription || '',
      website: organizationData.website || '',
      address: {
        street: organizationData.address?.street || '',
        city: organizationData.address?.city || '',
        state: organizationData.address?.state || '',
        zipCode: organizationData.address?.zipCode || '',
      },
      contactEmail: organizationData.contactEmail || '',
      contactPhone: organizationData.contactPhone || '',
      logoURL: organizationData.logoURL || '',
      adminIds: [createdByUid],
      inviteCode: inviteCode,
      status: 'active', // Auto-active for open registration (can be changed to 'trial', 'expired', etc. for future subscription features)
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: createdByUid,
    });

    // Log initial invite code to history
    await logInviteCodeHistory(orgRef.id, {
      action: 'created',
      oldCode: null,
      newCode: inviteCode,
      changedBy: createdByUid,
      changedByName: createdByName || 'Unknown',
    });

    return orgRef.id;
  } catch (error) {
    console.error('Error creating organization:', error);
    throw new Error('Failed to create organization: ' + error.message);
  }
};

/**
 * Get organization by ID
 * @param {string} organizationId
 * @returns {Promise<Object>} - Organization data with ID
 */
export const getOrganizationById = async (organizationId) => {
  try {
    const orgDoc = await getDoc(doc(db, COLLECTION_NAME, organizationId));

    if (!orgDoc.exists()) {
      throw new Error('Organization not found');
    }

    return {
      organizationId: orgDoc.id,
      ...orgDoc.data(),
    };
  } catch (error) {
    console.error('Error getting organization:', error);
    throw new Error('Failed to get organization: ' + error.message);
  }
};

/**
 * Get all active organizations (for volunteer registration dropdown)
 * @returns {Promise<Array>} - Array of active organizations
 */
export const getActiveOrganizations = async () => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('status', '==', 'active'),
      orderBy('organizationName', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const organizations = [];

    querySnapshot.forEach((doc) => {
      organizations.push({
        organizationId: doc.id,
        ...doc.data(),
      });
    });

    return organizations;
  } catch (error) {
    console.error('Error getting active organizations:', error);
    throw new Error('Failed to get organizations: ' + error.message);
  }
};

/**
 * Get all organizations (for platform admin)
 * @returns {Promise<Array>} - Array of all organizations
 */
export const getAllOrganizations = async () => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const organizations = [];

    querySnapshot.forEach((doc) => {
      organizations.push({
        organizationId: doc.id,
        ...doc.data(),
      });
    });

    return organizations;
  } catch (error) {
    console.error('Error getting all organizations:', error);
    throw new Error('Failed to get organizations: ' + error.message);
  }
};

/**
 * Update organization
 * @param {string} organizationId
 * @param {Object} updates - Fields to update
 * @returns {Promise<void>}
 */
export const updateOrganization = async (organizationId, updates) => {
  try {
    const orgRef = doc(db, COLLECTION_NAME, organizationId);
    await updateDoc(orgRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating organization:', error);
    throw new Error('Failed to update organization: ' + error.message);
  }
};

/**
 * Approve organization (platform admin only)
 * @param {string} organizationId
 * @param {string} approvedByUid - Platform admin user ID
 * @returns {Promise<void>}
 */
export const approveOrganization = async (organizationId, approvedByUid) => {
  try {
    await updateOrganization(organizationId, {
      status: 'active',
      approvedBy: approvedByUid,
      approvedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error approving organization:', error);
    throw new Error('Failed to approve organization: ' + error.message);
  }
};

/**
 * Suspend organization (platform admin only)
 * @param {string} organizationId
 * @returns {Promise<void>}
 */
export const suspendOrganization = async (organizationId) => {
  try {
    await updateOrganization(organizationId, {
      status: 'suspended',
    });
  } catch (error) {
    console.error('Error suspending organization:', error);
    throw new Error('Failed to suspend organization: ' + error.message);
  }
};

/**
 * Delete organization (platform admin only)
 * @param {string} organizationId
 * @returns {Promise<void>}
 */
export const deleteOrganization = async (organizationId) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, organizationId));
  } catch (error) {
    console.error('Error deleting organization:', error);
    throw new Error('Failed to delete organization: ' + error.message);
  }
};

/**
 * Add admin to organization
 * @param {string} organizationId
 * @param {string} userId - User ID to add as admin
 * @returns {Promise<void>}
 */
export const addOrganizationAdmin = async (organizationId, userId) => {
  try {
    const org = await getOrganizationById(organizationId);
    const adminIds = org.adminIds || [];

    if (!adminIds.includes(userId)) {
      adminIds.push(userId);
      await updateOrganization(organizationId, { adminIds });
    }
  } catch (error) {
    console.error('Error adding organization admin:', error);
    throw new Error('Failed to add organization admin: ' + error.message);
  }
};

/**
 * Remove admin from organization
 * @param {string} organizationId
 * @param {string} userId - User ID to remove as admin
 * @returns {Promise<void>}
 */
export const removeOrganizationAdmin = async (organizationId, userId) => {
  try {
    const org = await getOrganizationById(organizationId);
    const adminIds = (org.adminIds || []).filter(id => id !== userId);

    await updateOrganization(organizationId, { adminIds });
  } catch (error) {
    console.error('Error removing organization admin:', error);
    throw new Error('Failed to remove organization admin: ' + error.message);
  }
};

/**
 * Regenerate invite code for an organization
 * @param {string} organizationId
 * @param {string} userId - User ID who is regenerating the code
 * @param {string} userName - Display name of the user
 * @returns {Promise<string>} - New invite code
 */
export const regenerateInviteCode = async (organizationId, userId = '', userName = '') => {
  try {
    // Get current invite code before updating
    const org = await getOrganizationById(organizationId);
    const oldInviteCode = org.inviteCode;

    const newInviteCode = generateInviteCode();
    await updateOrganization(organizationId, { inviteCode: newInviteCode });

    // Log the change to history
    await logInviteCodeHistory(organizationId, {
      action: 'regenerated',
      oldCode: oldInviteCode,
      newCode: newInviteCode,
      changedBy: userId || 'system',
      changedByName: userName || 'System',
    });

    return newInviteCode;
  } catch (error) {
    console.error('Error regenerating invite code:', error);
    throw new Error('Failed to regenerate invite code: ' + error.message);
  }
};

/**
 * Verify organization by invite code
 * @param {string} inviteCode - Invite code to verify
 * @returns {Promise<Object|null>} - Organization data if found, null otherwise
 */
export const verifyOrganizationByInviteCode = async (inviteCode) => {
  try {
    console.log('🔵 organizationService: Verifying invite code:', inviteCode.toUpperCase());

    // First try to find ANY organization with this invite code (ignore status)
    const qAny = query(
      collection(db, COLLECTION_NAME),
      where('inviteCode', '==', inviteCode.toUpperCase()),
      limit(1)
    );

    console.log('🔵 organizationService: Checking if invite code exists (any status)...');
    const anySnapshot = await getDocs(qAny);
    console.log('🔵 organizationService: Found', anySnapshot.size, 'organizations with this code (any status)');

    if (!anySnapshot.empty) {
      const anyDoc = anySnapshot.docs[0];
      console.log('🔵 organizationService: Organization found with status:', anyDoc.data().status);
    }

    // Now query with status filter
    const q = query(
      collection(db, COLLECTION_NAME),
      where('inviteCode', '==', inviteCode.toUpperCase()),
      where('status', '==', 'active'),
      limit(1) // Required by Firestore security rules for invite code verification
    );

    console.log('🔵 organizationService: Executing query with status=active filter...');
    const querySnapshot = await getDocs(q);
    console.log('🔵 organizationService: Query returned', querySnapshot.size, 'documents');

    if (querySnapshot.empty) {
      console.log('❌ organizationService: No active organization found with invite code:', inviteCode.toUpperCase());
      return null;
    }

    const doc = querySnapshot.docs[0];
    const orgData = {
      organizationId: doc.id,
      ...doc.data(),
    };
    console.log('✅ organizationService: Found organization:', orgData);
    return orgData;
  } catch (error) {
    console.error('❌ organizationService: Error verifying organization by invite code:', error);
    throw new Error('Failed to verify invite code: ' + error.message);
  }
};

/**
 * Log invite code change to history
 * @param {string} organizationId
 * @param {Object} historyData - History entry data
 * @returns {Promise<void>}
 */
const logInviteCodeHistory = async (organizationId, historyData) => {
  try {
    const historyRef = collection(db, COLLECTION_NAME, organizationId, 'inviteCodeHistory');
    await addDoc(historyRef, {
      ...historyData,
      timestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error logging invite code history:', error);
    // Don't throw - we don't want to fail the main operation if history logging fails
  }
};

/**
 * Get invite code history for an organization
 * @param {string} organizationId
 * @returns {Promise<Array>} - Array of history entries
 */
export const getInviteCodeHistory = async (organizationId) => {
  try {
    const historyRef = collection(db, COLLECTION_NAME, organizationId, 'inviteCodeHistory');
    const q = query(historyRef, orderBy('timestamp', 'desc'));

    const querySnapshot = await getDocs(q);
    const history = [];

    querySnapshot.forEach((doc) => {
      history.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return history;
  } catch (error) {
    console.error('Error getting invite code history:', error);
    throw new Error('Failed to get invite code history: ' + error.message);
  }
};
